import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ThemeDefinition, ThemeModePalette } from '@/utils/themeDerivation';
import { DEFAULT_THEME } from '@/utils/themeDerivation';

export function useThemeDraft(initial: ThemeDefinition = DEFAULT_THEME) {
  const [savedTheme, setSavedTheme] = useState<ThemeDefinition>(initial);
  const [draftTheme, setDraftTheme] = useState<ThemeDefinition>(initial);

  const isDirty = useMemo(
    () => JSON.stringify(savedTheme) !== JSON.stringify(draftTheme),
    [savedTheme, draftTheme],
  );

  const resetDraft = useCallback(() => {
    setDraftTheme(savedTheme);
  }, [savedTheme]);

  const commitDraft = useCallback((theme: ThemeDefinition) => {
    setSavedTheme(theme);
    setDraftTheme(theme);
  }, []);

  const loadTheme = useCallback((theme: ThemeDefinition) => {
    setSavedTheme(theme);
    setDraftTheme(theme);
  }, []);

  const updatePalette = useCallback(
    (mode: 'light' | 'dark', field: keyof ThemeModePalette, value: string) => {
      setDraftTheme((prev) => ({
        ...prev,
        [mode]: { ...prev[mode], [field]: value },
      }));
    },
    [],
  );

  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  return {
    savedTheme,
    draftTheme,
    isDirty,
    resetDraft,
    commitDraft,
    loadTheme,
    updatePalette,
  };
}
